import apiClient from '@/api';
import type {
  EventTableRow,
  RebalanceScheduler,
  TimelineEvent,
} from '../types';
import type { EligibleUniverseJSON } from '../../../utils/adapters/eligibleUniverseAdapter';

// Per-request timeout for step 2's scheduler calls, overriding the axios default
// (60s). 3 minutes gives slow / shared connections — and a heavy ClickHouse year
// on the server side — room to finish rather than failing purely on time. Scoped
// to these calls so a hung request ELSEWHERE in the app still fails fast at 60s.
const STEP2_TIMEOUT_MS = 180_000;

// The scheduler config the two endpoints validate (1:1 with the backend
// RebalanceSchedulerConfig). The store holds it under rebalance_scheduler.
type SchedulerConfig = {
  recon_scheduler: RebalanceScheduler['recon_scheduler'];
  is_rebal_activated: boolean;
  // null when rebalancing is off (matches the backend activation contract).
  rebal_scheduler: RebalanceScheduler['rebal_scheduler'] | null;
};

interface DatesPayload {
  start_date: string;
  end_date: string;
  scheduler: SchedulerConfig;
}

interface SchedulerDatesResponse {
  recon_dates: Array<string>;
  rebal_dates: Array<string>;
  // Parallel to recon_dates: each event's lagged DATA-AS-OF date. Optional so an
  // API build predating it still parses.
  recon_asof_dates?: Array<string>;
}

// POST /v1/scheduler/dates — runs ONLY the scheduler (no DB), so it's cheap
// enough to call on every input change. recon_dates ⊂ rebal_dates; the
// inter-reconstitution rebalances are rebal_dates − recon_dates.
export async function fetchScheduleDates(payload: DatesPayload): Promise<{
  reconstitutionEvents: Array<TimelineEvent>;
  rebalanceEvents: Array<TimelineEvent>;
  /** recon date → its lagged DATA-AS-OF date (the snapshot the engine reads to
   *  select constituents at that reconstitution). */
  reconAsOfByDate: Record<string, string>;
}> {
  const res = await apiClient.post<SchedulerDatesResponse>(
    '/scheduler/dates',
    { ...payload, exchange: 'XNYS' },
    { timeout: STEP2_TIMEOUT_MS },
  );
  const reconSet = new Set(res.data.recon_dates);
  const asOf = res.data.recon_asof_dates ?? [];
  return {
    reconstitutionEvents: res.data.recon_dates.map((date) => ({
      date,
      type: 'reconstitution' as const,
      description: 'Reconstitution',
    })),
    rebalanceEvents: res.data.rebal_dates
      .filter((d) => !reconSet.has(d))
      .map((date) => ({
        date,
        type: 'rebalance' as const,
        description: 'Rebalance',
      })),
    reconAsOfByDate: Object.fromEntries(
      res.data.recon_dates.map((d, i) => [d, asOf[i] ?? d]),
    ),
  };
}

interface YearStatsPayload {
  start_date: string;
  end_date: string;
  scheduler: SchedulerConfig;
  // Full EligibleUniverseConfig from step 1 (same shape buildStrategyConfig
  // emits), so the per-year stats reflect the user's categorical/numerical
  // exclusions — the post-filter universe, not just the starting universe.
  eligible_universe: EligibleUniverseJSON;
  year: number;
}

interface SnapshotStatRow {
  effective_date: string;
  type: string;
  constituents: number;
  assets_added: number;
  assets_dropped: number;
  turnover_estimate: number;
}

interface SnapshotStatsResponse {
  rows: Array<SnapshotStatRow>;
}

// POST /v1/scheduler/snapshot-stats — per-reconstitution universe stats for ONE
// year (lazy: only the year the user expands). Year-scoped so the ClickHouse
// work stays cheap.
export async function fetchYearStats(
  payload: YearStatsPayload,
): Promise<Array<EventTableRow>> {
  const res = await apiClient.post<SnapshotStatsResponse>(
    '/scheduler/snapshot-stats',
    { ...payload, exchange: 'XNYS' },
    { timeout: STEP2_TIMEOUT_MS },
  );
  return res.data.rows.map((r) => ({
    effectiveDate: r.effective_date,
    type: r.type === 'rebalance' ? 'rebalance' : 'reconstitution',
    constituents: r.constituents,
    assetsAdded: r.assets_added,
    assetsDropped: r.assets_dropped,
    turnoverEstimate: r.turnover_estimate,
  }));
}
